import type { PendingSettlementsSummaryData } from '../../utils/client-reports/types';
import {
  EmptySection,
  Metric,
  MetricStrip,
  SectionHeader,
} from './ReportBlocks';
import { useReportLayout } from './reportLayout';
import { useReportFormat } from './useReportFormat';

// Mismos colores que el estado en las tablas: rojo para lo crítico, ámbar para
// lo vencido y gris para lo que está por vencer.
const STATUS_CLASSES = {
  critical: 'bg-danger-500',
  overdue: 'bg-warning-500',
  dueSoon: 'bg-default-400',
} as const;

// Resumen de liquidaciones por recibir: cuántos embarques sin liquidar están
// críticos, vencidos o por vencer.
const PendingSettlementsSummaryView = ({
  number: sectionNumber,
  data,
}: {
  number: number;
  data: PendingSettlementsSummaryData;
}) => {
  const { t, number, plural } = useReportFormat();
  const layout = useReportLayout();
  const title = t('Resumen');
  const total = {
    shipments:
      data.critical.shipments + data.overdue.shipments + data.dueSoon.shipments,
    ceq: data.critical.ceq + data.overdue.ceq + data.dueSoon.ceq,
  };
  const aside = t('Plazo de liquidación: {{days}} días desde la ETA', {
    days: data.termDays,
  });
  const withoutEtaNote =
    data.withoutEta > 0
      ? plural(
          data.withoutEta,
          '{{n}} embarque sin liquidar no tiene ETA, así que no se puede saber si está atrasado. No se incluye.',
          '{{n}} embarques sin liquidar no tienen ETA, así que no se puede saber si están atrasados. No se incluyen.'
        )
      : null;

  if (total.shipments === 0) {
    return (
      <section className='flex flex-col gap-6'>
        <SectionHeader number={sectionNumber} title={title} aside={aside} />
        <EmptySection>
          {t('No hay liquidaciones vencidas ni por vencer.')}
        </EmptySection>
        {withoutEtaNote ? (
          <p className='text-xs text-default-500'>{withoutEtaNote}</p>
        ) : null}
      </section>
    );
  }

  const statuses = [
    {
      key: 'critical' as const,
      label: t('Críticas'),
      bucket: data.critical,
      detail: t('Más de 45 días desde la ETA'),
    },
    {
      key: 'overdue' as const,
      label: t('Vencidas'),
      bucket: data.overdue,
      detail: t('Entre 30 y 45 días desde la ETA'),
    },
    {
      key: 'dueSoon' as const,
      label: t('Por vencer'),
      bucket: data.dueSoon,
      detail: t('Vencen en los próximos 7 días'),
    },
  ];

  return (
    <section className='flex flex-col gap-6'>
      <SectionHeader number={sectionNumber} title={title} aside={aside} />

      <MetricStrip className={layout.kpiGrid}>
        <Metric
          label={t('Fuera de plazo')}
          value={number(total.shipments)}
          suffix={total.shipments === 1 ? t('embarque') : t('embarques')}
          detail={t('{{ceq}} cajas eq. · Críticas y vencidas', {
            ceq: number(total.ceq),
          })}
        />
        {statuses.map((status) => (
          <Metric
            key={status.key}
            label={status.label}
            value={number(status.bucket.shipments)}
            suffix={status.bucket.shipments === 1 ? t('embarque') : t('embarques')}
            tone={
              status.key === 'critical' && status.bucket.shipments > 0
                ? 'danger'
                : status.bucket.shipments > 0
                  ? 'default'
                  : 'muted'
            }
            detail={`${t('{{ceq}} cajas eq.', {
              ceq: number(status.bucket.ceq),
            })} · ${status.detail}`}
          />
        ))}
      </MetricStrip>

      <div className='flex flex-col gap-3'>
        <div className='flex h-2.5 w-full overflow-hidden rounded-full bg-default-100'>
          {statuses.map((status) =>
            status.bucket.shipments > 0 ? (
              <div
                key={status.key}
                className={STATUS_CLASSES[status.key]}
                style={{
                  width: `${(status.bucket.shipments / total.shipments) * 100}%`,
                }}
              />
            ) : null
          )}
        </div>
        <div className='flex flex-wrap gap-x-4 gap-y-1 text-xs text-default-500'>
          {statuses.map((status) => (
            <span
              key={status.key}
              className='flex items-center gap-1.5 whitespace-nowrap'
            >
              <span
                className={`h-2.5 w-2.5 rounded-sm ${STATUS_CLASSES[status.key]}`}
              />
              {status.label} · {number(status.bucket.shipments)}
            </span>
          ))}
        </div>
      </div>

      {withoutEtaNote ? (
        <p className='text-xs text-default-500'>{withoutEtaNote}</p>
      ) : null}
    </section>
  );
};

export default PendingSettlementsSummaryView;
